// PHA-567 inline sparkline. Pure SVG so we don't pull a chart lib.
// Points come from PatientAiInsight.sparkline_series — DETERMINISTIC,
// computed in trends.py on the backend, never produced by the LLM.

import React from 'react';

interface TrendSparklineProps {
  points: number[];
  width?: number;
  height?: number;
  color?: string;
  strokeWidth?: number;
  className?: string;
  ariaLabel?: string;
}

export const TrendSparkline: React.FC<TrendSparklineProps> = ({
  points,
  width = 90,
  height = 22,
  color = 'currentColor',
  strokeWidth = 1.5,
  className,
  ariaLabel,
}) => {
  if (!points || points.length < 2) {
    return (
      <svg width={width} height={height} className={className} aria-label={ariaLabel || 'sparkline'}>
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke={color}
          strokeWidth={1}
          opacity={0.3}
        />
      </svg>
    );
  }
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const stepX = width / (points.length - 1);
  const path = points
    .map((p, i) => {
      const x = i * stepX;
      // Invert so higher values draw higher on screen (SVG y grows down).
      const y = height - ((p - min) / range) * height;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg
      width={width}
      height={height}
      className={className}
      role="img"
      aria-label={ariaLabel || `sparkline ${points.length} points`}
    >
      <path d={path} fill="none" stroke={color} strokeWidth={strokeWidth} />
    </svg>
  );
};

export default TrendSparkline;
